<div class="ps-large-y">
  <h2>{{ 'MMB.apis.form.passport.title' | finLocalization }}</h2>
</div>

@if ((passportVerifierEnabled$ | async) === true) {
  @if ((documentSwiped$ | async) === true) {
    <fcom-notification class="ps-xlarge-b" [theme]="NotificationTheme.SUCCESS">
      <p>{{ 'MMB.apis.form.passport.preCheckedSuccess' | finLocalization }}</p>
    </fcom-notification>

    <ng-container *ngTemplateOutlet="buttons"></ng-container>
  } @else {
    <div class="custom-notification nordic-blue-50-bg nordic-blue-900-text rounded-large ps-medium">
      <h3 class="font-heading-5 ps-xsmall-b">
        {{
          ((allDetailsProvided$ | async) === false
            ? (showScanButton$ | async) === true
              ? 'MMB.apis.form.passport.scanUploadTitle'
              : 'MMB.apis.form.passport.uploadTitle'
            : 'MMB.apis.form.passport.notVerifiedTitle'
          ) | finLocalization
        }}
      </h3>
      <div class="notification-grid g-xsmall">
        <div class="notification-image">
          <fcom-icon [name]="SvgLibraryIcon.TRAVEL_DOCUMENTS"></fcom-icon>
        </div>
        <div
          class="notification-content font-body-1"
          [innerHTML]="
            ((allDetailsProvided$ | async) === false
              ? ('MMB.apis.form.passport.scanUploadInfo' | finLocalization)
              : ('MMB.apis.form.passport.notVerifiedDescription' | finLocalization)
            )
              | finAddClasses: { p: 'font-body-1', strong: 'bold-type' }
              | finSafe: 'html'
          "
        ></div>

        <div clasS="notification-buttons">
          <ng-container *ngTemplateOutlet="buttons"></ng-container>
        </div>
      </div>
    </div>
  }

  <ng-template #buttons>
    <div class="flex flex--column g-xsmall">
      <div class="flex flex--wrap g-xsmall button-group">
        @if ((showScanButton$ | async) === true) {
          <fcom-button
            data-testid="scan-button"
            class="flex--self-center"
            [icon]="{ name: SvgLibraryIcon.CAMERA, position: IconPosition.LEFT }"
            [text]="'MMB.apis.form.passport.scanButton' | finLocalization"
            [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
            (clickBtn)="openPassportVerifier(true)"
          ></fcom-button>
        }
        <fcom-button
          data-testid="upload-button"
          [icon]="{ name: SvgLibraryIcon.TRAVEL_DOCUMENTS, position: IconPosition.LEFT }"
          [text]="'MMB.apis.form.passport.uploadButton' | finLocalization"
          [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
          (clickBtn)="openPassportVerifier(false)"
        ></fcom-button>
      </div>
      <div
        class="font-body-2 nordic-blue-400-text"
        [innerHTML]="
          'MMB.apis.form.passport.supportedFormats'
            | finLocalization
            | finAddClasses: { p: 'font-body-2' }
            | finSafe: 'html'
        "
      ></div>
    </div>
  </ng-template>

  <div class="border-dashed ms-xlarge-t ms-medium-b"></div>
}

@if (passportForm) {
  <form #form>
    @if (passportForm.enabled || this.loading) {
      <fcom-notification
        data-testid="guide-notification"
        [class.ps-medium-t]="(passportVerifierEnabled$ | async) === false"
        [theme]="(passportVerifierEnabled$ | async) === true ? NotificationTheme.TRANSPARENT : NotificationTheme.INFO"
        [removeHorizontalPadding]="(passportVerifierEnabled$ | async) === true"
        [linkUrl]="'#'"
        [linkText]="'MMB.apis.form.passport.guide.linkText' | finLocalization"
        (linkClick)="showGuide()"
      >
        {{ 'MMB.apis.form.passport.notification' | finLocalization }}
      </fcom-notification>
    }

    <div class="ps-small-b font-body-1 nordic-blue-900-text align-right">
      {{ 'MMB.apis.form.passport.mandatoryFields' | finLocalization }}
    </div>

    <div class="flex flex--wrap">
      <fcom-text-input
        class="flex-1 input-field"
        data-testid="passport-lastname"
        [translationPrefix]="'MMB.apis.form.passport.lastName'"
        [parentForm]="passportForm"
        [controlName]="'lastName'"
        [displayMargin]="false"
      ></fcom-text-input>

      <fcom-text-input
        class="flex-1 input-field"
        data-testid="passport-firstname"
        [translationPrefix]="'MMB.apis.form.passport.firstName'"
        [parentForm]="passportForm"
        [controlName]="'firstName'"
        [displayMargin]="false"
      ></fcom-text-input>
    </div>

    <div class="flex flex--wrap">
      <fin-datepicker
        data-testid="passport-birthdate"
        class="flex-1 input-field"
        [translationPrefix]="'MMB.apis.form.passport.birthDate'"
        [parentForm]="passportForm"
        [controlName]="'birthDate'"
        [placeHolder]="'MMB.apis.form.passport.birthDate.placeholder'"
        [icon]="SvgLibraryIcon.CALENDAR"
        [minDate]="LocalDate.forDate(1900, 1, 1)"
        [startView]="'multi-year'"
        [startAt]="LocalDate.forDate(1980, 1, 1)"
        [theme]="DatepickerTheme.WEB_2"
        [displayMargin]="false"
      ></fin-datepicker>

      <fin-select-input
        class="flex-1 input-field"
        [translationPrefix]="'MMB.apis.form.gender'"
        [parentForm]="passportForm"
        [controlName]="'gender'"
        [options]="genders$ | async"
        [displayMargin]="false"
      >
      </fin-select-input>
    </div>

    <div class="flex flex--wrap">
      <fcom-text-input
        class="flex-1 input-field"
        data-testid="passport-number"
        [translationPrefix]="'MMB.apis.form.passport.number'"
        [parentForm]="passportForm"
        [controlName]="'number'"
        [displayMargin]="false"
      >
      </fcom-text-input>

      <fin-select-input
        class="flex-1 input-field"
        [translationPrefix]="'MMB.apis.form.nationality'"
        [parentForm]="passportForm"
        [controlName]="'nationality'"
        [options]="nationalities$ | async"
        [displayMargin]="false"
      >
      </fin-select-input>
    </div>

    <div
      class="flex flex--wrap"
      [class.ps-large-b]="!passportForm.controls.issuanceDate && !passportForm.disabled && !this.loading"
    >
      <fin-select-input
        class="flex-1 input-field"
        [controlName]="'issuingCountry'"
        [translationPrefix]="'MMB.apis.form.passport.issuingCountry'"
        [parentForm]="passportForm"
        [options]="countries$ | async"
        [displayMargin]="false"
      >
      </fin-select-input>

      <fin-datepicker
        class="flex-1 input-field"
        data-testid="passport-expiry-date"
        [parentForm]="passportForm"
        [controlName]="'expiryDate'"
        [placeHolder]="'MMB.apis.form.passport.expiryDate.placeholder'"
        [translationPrefix]="'MMB.apis.form.passport.expiryDate'"
        [icon]="SvgLibraryIcon.CALENDAR"
        [theme]="DatepickerTheme.WEB_2"
        [minDate]="LocalDate.now()"
        [maxDate]="LocalDate.now().plusYears(20)"
        [displayMargin]="false"
        [startView]="'multi-year'"
        [startAt]="LocalDate.now().plusYears(5)"
      ></fin-datepicker>
    </div>

    @if (passportForm.controls.issuanceDate) {
      <div class="flex flex--wrap" [class.ps-large-b]="!passportForm.disabled && !this.loading">
        <fin-datepicker
          class="flex-1 input-field"
          data-testid="passport-issuance-date"
          [parentForm]="passportForm"
          [controlName]="'issuanceDate'"
          [placeHolder]="'MMB.apis.form.passport.issuanceDate.placeholder'"
          [translationPrefix]="'MMB.apis.form.passport.issuanceDate'"
          [icon]="SvgLibraryIcon.CALENDAR"
          [theme]="DatepickerTheme.WEB_2"
          [minDate]="LocalDate.now().minusYears(20)"
          [maxDate]="LocalDate.now()"
          [startView]="'multi-year'"
          [startAt]="LocalDate.now().minusYears(5)"
          [displayMargin]="false"
        ></fin-datepicker>

        <div class="flex-1 input-field"></div>
      </div>
    }

    @if (passportForm.disabled && !this.loading) {
      <fcom-button
        data-testid="edit-button"
        class="ps-medium-t"
        [icon]="{ name: SvgLibraryIcon.EDIT, position: IconPosition.LEFT }"
        [text]="'MMB.apis.form.passport.manualEdit' | finLocalization"
        [theme]="ButtonTheme.LIGHT_SECONDARY_OUTLINE"
        (clickBtn)="enableAndScroll()"
      ></fcom-button>

      <div class="flex ps-xsmall-t ps-large-b">
        <fcom-icon class="flex--nogrow icon-small error-800-text" [name]="SvgLibraryIcon.ALERT_TRIANGLE"></fcom-icon>
        <div class="font-body-2 nordic-blue-400-text">
          {{ 'MMB.apis.form.passport.manualEditWarning' | finLocalization }}
        </div>
      </div>
    }

    <div role="presentation" class="border-top-dashed nordic-blue-100-border fill"></div>

    <div class="ps-large-y flex flex--end">
      <fcom-button
        data-testid="save-button"
        [type]="ButtonType.SUBMIT"
        [loading]="loading"
        (clickBtn)="saveAndContinue()"
        [text]="'MMB.apis.form.buttons.confirmSave' | finLocalization"
      ></fcom-button>
    </div>
  </form>
}

<fcom-modal
  *ngIf="guide$ | async as guide"
  [open]="guideModalOpen"
  [fitToContent]="true"
  [title]="guide.teaserTitle"
  (close)="guideModalOpen = false"
>
  <div class="pr-medium">
    <fin-content-image [imageData$]="guide$ | finMap: 'picture'" [scale]="false"></fin-content-image>
    <cms-unstyled-fragment
      class="font-body-1 bold-type"
      [showTitle]="false"
      [cmsContent$]="guide$"
    ></cms-unstyled-fragment>
  </div>
</fcom-modal>
